import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import unionBy from 'lodash/unionBy';
import get from 'lodash/get';
import { useApolloClient } from '@apollo/client';

import { ProductGroupDocument, ProductGroupFragment } from '@occc/graphql';
import { useFormSubmit } from '@occc-app/hooks/useFormSubmit';
import { useOpenState } from '@occc-app/hooks/useOpenState';
import { createProductGroupFormId } from '@occc-app/routes/ProductGroup/ProductGroupCreatePage/CreateProductGroupFormContainer';
import ExistingProductGroupsContainer from '@occc-app/routes/ProductGroup/components/ExistingProductGroupsContainer';
import {
  ProductGroupFormRenderCallback,
  ExistingProductGroupsRenderCallback,
} from '@occc-app/routes/ProductGroup/types';
import { actions } from '@occc-app/Offer/ProductGroups/redux';
import { useChainId } from '@occc-app/Chain/hooks';

import { ExistingProductGroups } from './ExistingProductGroups';
import { ProductGroupSelectEnhanced } from './ProductGroupSelectEnhanced';
import { OnAutoCompleteChange, OnSelectExistingProductGroup } from './types';

interface IProductGroupSelectEnhancedContainerProps {
  selectedProductGroups: string[];
  onChange: (selectedValues: string[]) => void;
  editMode: boolean;
  readonly: boolean;
  label?: string;
  ineligibleType?: boolean;
}

export const ProductGroupSelectEnhancedContainer: React.FC<
  IProductGroupSelectEnhancedContainerProps
> = (props) => {
  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const chainId = useChainId();
  const [open, onOpen, onClose] = useOpenState();
  const [storedProductGroups, setStoredProductGroups] = useState<ProductGroupFragment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const submitCreateProductGroupForm = useFormSubmit(createProductGroupFormId);

  const storeProductGroups = (productGroups: ProductGroupFragment[]) => {
    setStoredProductGroups(productGroups);
    dispatch(actions.productGroupsOK(productGroups));
  };

  // TODO: Change later - offer should contain info about its own product groups
  const getProductGroupsByIds = async () => {
    const requests = props.selectedProductGroups.map((id) =>
      apolloClient.query({
        query: ProductGroupDocument,
        variables: {
          filter: {
            idEquals: id,
            ...(chainId ? { chainIdEquals: chainId } : {}),
            isDeleted: false,
          },
        },
      }),
    );
    try {
      setLoading(true);
      const responses = await Promise.all(requests);
      const selectedProductGroupsDetails = responses
        .map((response) => get(response.data, 'productGroups.edges.0.node', null))
        .filter(Boolean);
      storeProductGroups(selectedProductGroupsDetails);
    } catch {
      storeProductGroups([]);
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    const makeRequests = props.selectedProductGroups.some(
      (id) => !unionBy(storedProductGroups, { id }),
    );
    if (makeRequests) {
      getProductGroupsByIds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedProductGroups]);

  const onSelect = React.useCallback<OnSelectExistingProductGroup>(
    (selectedProductGroup) => {
      if (!props.selectedProductGroups.includes(selectedProductGroup.id)) {
        props.onChange([...props.selectedProductGroups, selectedProductGroup.id]);
        storeProductGroups(unionBy([selectedProductGroup], storedProductGroups, 'id'));
      }
      onClose();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange, props.selectedProductGroups],
  );

  const onAutoCompleteChange = React.useCallback<OnAutoCompleteChange>(
    (pgs: ProductGroupFragment[]) => {
      const newSelectedProductGroups = pgs.map((pg) => pg.id);
      storeProductGroups(unionBy(pgs, storedProductGroups, 'id'));
      props.onChange(newSelectedProductGroups);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange],
  );

  const renderExistingProductGroups = React.useCallback<ExistingProductGroupsRenderCallback>(
    (configuredValues, productGroups) => {
      return (
        <ExistingProductGroups existingProductGroups={productGroups} onSelectExisting={onSelect} />
      );
    },
    [onSelect],
  );
  const renderExistingProductGroupsContainer = React.useCallback<ProductGroupFormRenderCallback>(
    (values) => (
      <ExistingProductGroupsContainer
        render={renderExistingProductGroups}
        configuredValues={values.values}
      />
    ),
    [renderExistingProductGroups],
  );

  return (
    <ProductGroupSelectEnhanced
      loading={loading}
      productGroups={storedProductGroups}
      selectedProductGroupIds={props.selectedProductGroups}
      onSelect={onSelect}
      onChange={onAutoCompleteChange}
      onSubmit={submitCreateProductGroupForm}
      renderExistingProductGroupsContainer={renderExistingProductGroupsContainer}
      editMode={props.editMode}
      readonly={props.readonly}
      label={props.label}
      ineligibleType={props.ineligibleType}
      open={open}
      showModal={onOpen}
      hideModal={onClose}
    />
  );
};
